import type { Boost, Predicate, SearchDescriptor } from '@shared/core/search';

interface TransportRequest<TDoc> {
  readonly descriptor: SearchDescriptor<TDoc>;
  readonly query: string;
  readonly filter: Predicate<TDoc>;
  readonly hitsPerPage: number;
  readonly boosts?: readonly Boost<TDoc>[];
  readonly signal?: AbortSignal;
}

export interface SearchTransport {
  search<TDoc>(request: TransportRequest<TDoc>): Promise<readonly TDoc[]>;
}

export type SearchDocument = Readonly<Record<string, unknown>>;

/** One collection's query inside a multi-collection request. */
export interface MultiSearchLeg {
  readonly collection: string;
  readonly query: string;
  readonly filter: Predicate<SearchDocument>;
  readonly hitsPerPage: number;
  /** Fields whose value counts come back in `facetCounts`. */
  readonly facets?: readonly string[];
  /** Typesense matches on these fields; Algolia ranks by its index settings. */
  readonly typesense: {
    readonly queryBy: string;
    readonly queryByWeights?: string;
    readonly numTypos?: string;
    /** Tokens of context kept on each side of a match in a highlight snippet. */
    readonly highlightAffixNumTokens?: number;
  };
}

/** `field → snippet`, where the snippet wraps each matched token in `<mark>`. */
export type SearchHighlight = Readonly<Record<string, { readonly snippet?: string }>>;

export interface MultiSearchHit {
  readonly document: SearchDocument;
  /** Relevance score; comparable across legs of one request. */
  readonly score: number;
  readonly highlight: SearchHighlight;
}

export interface FacetCount {
  readonly value: string;
  readonly count: number;
}

export interface MultiSearchResult {
  /** Total matches for the leg, not the length of `hits`. */
  readonly found: number;
  readonly hits: readonly MultiSearchHit[];
  readonly facetCounts: Readonly<Record<string, readonly FacetCount[]>>;
  /** Set when the engine rejected this leg; the other legs still answer. */
  readonly error?: string;
}

export interface MultiSearchTransport {
  multiSearch(legs: readonly MultiSearchLeg[], signal?: AbortSignal): Promise<readonly MultiSearchResult[]>;
}

export class SearchAuthError extends Error {
  constructor(status: number) {
    super(`Search credentials rejected (${status})`);
    this.name = 'SearchAuthError';
  }
}

export const isAuthStatus = (status: number): boolean => status === 401 || status === 403;
